import type { RegulatoryFileListResult } from '../types/regulatoryFiles'

interface BackendResponse<T> {
  data: T
  error: string
}

/**
 * Fetches regulatory file metadata for a section; download URLs are minted
 * separately per click via `getFileDownloadUrl`.
 * @param {string} section Backend section key (e.g. 'market-data')
 * @param {{ year?: string; month?: string }} filters Optional year/month filter
 */
export const getFileList = async (
  section: string,
  filters: { year?: string; month?: string } = {},
): Promise<RegulatoryFileListResult> => {
  try {
    const params = new URLSearchParams()
    if (filters.year) params.set('year', filters.year)
    if (filters.month) params.set('month', filters.month)
    const query = params.toString() ? `?${params.toString()}` : ''

    const response = await fetch(`${process.env.GATSBY_API_URL}/api/files/${section}${query}`)
    const body: BackendResponse<RegulatoryFileListResult> = await response.json()

    if (body.error) {
      throw new Error(body.error)
    }

    return body.data
  } catch (error) {
    console.log(error)
    return { files: [], openInNewTab: false, filterable: false }
  }
}

/**
 * Builds the backend download route for a single file, which 302s to a
 * freshly-minted presigned URL so links never expire in a cached list.
 * @param {string} section Backend section key
 * @param {string} key Full S3 object key from the file metadata
 */
export const getFileDownloadUrl = (section: string, key: string): string =>
  `${process.env.GATSBY_API_URL}/api/files/${section}/download?key=${encodeURIComponent(key)}`
